import { useState } from "react";
import { ArrowRight, ArrowUpRight, Check } from "lucide-react";
import { useLanguage } from "../context/LanguageContext";
import { submitContact } from "../lib/api";

const CONTACT_EMAIL = "info@cabounstoppable.com";

const fieldClass =
  "w-full bg-transparent border-0 border-b border-white/20 px-0 py-3 text-[15px] text-white placeholder-white/30 focus:outline-none focus:border-yellow-400 transition-colors duration-300 rounded-none";

export default function Contact() {
  const { t } = useLanguage();
  const [form, setForm] = useState({ name: "", email: "", message: "", website: "" });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState(false);

  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(false);
    try {
      await submitContact(form);
      setSent(true);
      setForm({ name: "", email: "", message: "", website: "" });
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <main data-testid="contact-page" className="bg-black min-h-screen pt-28 md:pt-36 relative overflow-hidden">
      <div className="absolute inset-0 grid-lines pointer-events-none" aria-hidden="true" />
      <div className="noise" aria-hidden="true" />

      <div className="relative max-w-[1500px] mx-auto px-6 md:px-10 pb-24 md:pb-32">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-14 lg:gap-0">
          {/* ——— Left: brand / direct contact ——— */}
          <div className="lg:col-span-5 lg:pr-16 fade-up">
            <p className="overline mb-5" data-testid="contact-overline">{t.contact.overline}</p>
            <h1 className="h-display text-white text-5xl sm:text-6xl lg:text-[5.2rem]">
              {t.contact.title1}
              <br />
              <span className="text-yellow-400">{t.contact.title2}</span>
            </h1>
            <p className="mt-8 text-white/60 max-w-sm text-base leading-relaxed">
              {t.contact.intro}
            </p>

            <div className="mt-12 md:mt-14">
              <p className="text-[10px] font-bold tracking-[0.3em] uppercase text-white/40 mb-3">
                {t.contact.emailLabel}
              </p>
              <a
                href={`mailto:${CONTACT_EMAIL}`}
                data-testid="contact-email-link"
                className="group inline-flex items-baseline gap-2"
              >
                <span className="cta-underline text-white text-lg sm:text-xl md:text-2xl font-editorial font-semibold tracking-tight break-all">
                  {CONTACT_EMAIL}
                </span>
                <ArrowUpRight className="w-4 h-4 text-yellow-400 shrink-0 translate-y-[2px] transition-transform duration-300 group-hover:translate-x-[3px] group-hover:-translate-y-[1px]" />
              </a>
              <p className="mt-3 text-white/40 text-sm">{t.contact.emailNote}</p>
            </div>

            <div className="mt-12 md:mt-14 pt-8 border-t border-white/10">
              <p className="text-white/70 text-[11px] font-bold tracking-[0.3em] uppercase">
                {t.hero.location}
              </p>
              <p className="mt-2 text-white/35 text-[11px] tracking-[0.25em]">
                {t.hero.coords}
              </p>
            </div>
          </div>

          {/* ——— Divider ——— */}
          <div className="hidden lg:block lg:col-span-1 relative" aria-hidden="true">
            <div className="absolute left-1/2 top-2 bottom-0 w-px bg-white/10" />
          </div>

          {/* ——— Right: form ——— */}
          <div className="lg:col-span-6 lg:pl-12 fade-up delay-200">
            {sent ? (
              <div data-testid="contact-success" className="lg:pt-2">
                <div className="inline-flex items-center justify-center w-12 h-12 border border-yellow-400/60 mb-8">
                  <Check className="w-5 h-5 text-yellow-400" />
                </div>
                <h2 className="h-display text-white text-4xl sm:text-5xl">
                  {t.contact.successTitle}
                </h2>
                <p className="mt-6 text-white/60 max-w-md leading-relaxed">
                  {t.contact.successBody}
                </p>
                <button
                  type="button"
                  data-testid="contact-send-another"
                  onClick={() => setSent(false)}
                  className="cta-line cta-line mt-10 text-white/80 hover:text-white inline-flex items-center gap-2 text-[11px] font-bold tracking-[0.22em] uppercase border-b border-white/25 pb-1 hover:border-yellow-400 transition-colors"
                >
                  {t.contact.sendAnother} <ArrowRight className="w-3.5 h-3.5 btn-arrow" />
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} data-testid="contact-form" noValidate={false}>
                <p className="overline mb-10">{t.contact.formOverline}</p>

                <div className="space-y-9 md:space-y-10">
                  <div>
                    <label htmlFor="contact-name" className="block text-[10px] font-bold tracking-[0.3em] uppercase text-white/40 mb-1">
                      {t.contact.nameLabel}
                    </label>
                    <input
                      id="contact-name"
                      required
                      name="name"
                      autoComplete="name"
                      value={form.name}
                      onChange={onChange}
                      placeholder={t.contact.namePlaceholder}
                      data-testid="contact-name"
                      className={fieldClass}
                    />
                  </div>

                  <div>
                    <label htmlFor="contact-email" className="block text-[10px] font-bold tracking-[0.3em] uppercase text-white/40 mb-1">
                      {t.contact.emailFieldLabel}
                    </label>
                    <input
                      id="contact-email"
                      required
                      type="email"
                      name="email"
                      autoComplete="email"
                      value={form.email}
                      onChange={onChange}
                      placeholder={t.contact.emailPlaceholder}
                      data-testid="contact-email"
                      className={fieldClass}
                    />
                  </div>

                  <div>
                    <label htmlFor="contact-message" className="block text-[10px] font-bold tracking-[0.3em] uppercase text-white/40 mb-1">
                      {t.contact.messageLabel}
                    </label>
                    <textarea
                      id="contact-message"
                      required
                      name="message"
                      value={form.message}
                      onChange={onChange}
                      placeholder={t.contact.messagePlaceholder}
                      rows={5}
                      data-testid="contact-message"
                      className={`${fieldClass} resize-none`}
                    />
                  </div>

                  {/* Honeypot — hidden from real users */}
                  <input
                    type="text"
                    name="website"
                    value={form.website}
                    onChange={onChange}
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    className="absolute left-[-9999px] top-[-9999px] h-0 w-0 opacity-0"
                  />
                </div>

                {error && (
                  <p data-testid="contact-error" className="mt-8 text-sm text-red-400/90 border-l-2 border-red-400/60 pl-4">
                    {t.contact.sentError}
                  </p>
                )}

                <div className="mt-12">
                  <button
                    type="submit"
                    disabled={loading}
                    data-testid="contact-submit"
                    className="btn-cabo btn-cabo--yellow disabled:opacity-50 disabled:pointer-events-none w-full sm:w-auto"
                  >
                    {loading ? `${t.contact.sending}…` : t.contact.send}
                    <ArrowRight className="w-4 h-4 btn-arrow" />
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </main>
  );
}
